import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import UserSearch from './UserSearch';
import UserList from './UserList';
import { API_BASE_URL } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserEdit from './UserEdit';

const UserAdministration = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [logoutingUser, setLogoutingUser] = useState(null);
    const { isAdmin, token } = useAuth();
    const navigate = useNavigate();

    // Initial filters
    const [filters, setFilters] = useState({
        search: '',
        sort_by: 'created_at',
        order: 'desc'
    });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams(filters).toString();

            const response = await fetch(`${API_BASE_URL}/api/auth/users?${queryParams}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error(t('UserAdministration.error_permission'));
                }
                throw new Error(t('UserAdministration.error_load'));
            }

            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [filters, t, token]);

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/');
            return;
        }
        fetchUsers();
    }, [fetchUsers, isAdmin, navigate]);

    const handleSearch = (newFilters) => {
        setFilters(newFilters);
    };

    const handleUserClick = (user) => {
        setSelectedUser(user);
    };

    const handleSuccess = () => {
        setSuccessMessage(t('UserAdministration.success_password'));
        setTimeout(() => setSuccessMessage(''), 3000);
    };

    const handleForceLogout = async (userId, userName) => {
        const confirmed = window.confirm(`¿Cerrar sesión de ${userName} en todos sus dispositivos?`);
        if (!confirmed) return;

        setLogoutingUser(userId);
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/sessions/invalidate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ user_id: userId })
            });

            if (response.ok) {
                setSuccessMessage(`Usuario ${userName} desconectado exitosamente`);
                fetchUsers(); // Refresh list
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Error al desconectar usuario');
                setTimeout(() => setError(null), 3000);
            }
        } catch (err) {
            console.error('Logout error:', err);
            setError('Error de red al intentar desconectar usuario');
            setTimeout(() => setError(null), 3000);
        } finally {
            setLogoutingUser(null);
        }
    };

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{t('UserAdministration.title')}</h2>
                    <div className="text-sm text-gray-500">
                        {t('UserAdministration.subtitle_count', { count: users.length })}
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-100">
                        {error}
                    </div>
                )}

                {successMessage && (
                    <div className="p-4 bg-green-50 text-green-700 rounded-lg border border-green-100">
                        {successMessage}
                    </div>
                )}

                <UserSearch onSearch={handleSearch} />
                <UserList
                    users={users}
                    loading={loading}
                    onUserClick={handleUserClick}
                    onForceLogout={handleForceLogout}
                    logoutingUser={logoutingUser}
                />

                {selectedUser && (
                    <UserEdit
                        user={selectedUser}
                        onClose={() => setSelectedUser(null)}
                        onSuccess={handleSuccess}
                    />
                )}
            </div>
        </div>
    );
};

export default UserAdministration;
// Revisado: 2025-12-16
